import React from 'react';
import clsx from 'clsx';
import { FilterableSelect } from './FilterableSelect';
import {
  PHONE_COUNTRY_OPTIONS,
  getPhoneCountry,
  getPhoneMask,
  formatNationalPhone,
  parsePhoneEntry,
} from '../../utils/phone';

/** Room for the flag, the country name and the code in the open list. */
const COUNTRY_PANEL_MIN_WIDTH = 260;

/**
 * Composite phone field: a calling code picker plus the national number.
 *
 * The two halves are kept as separate values instead of one masked string so
 * the code stays editable and can never be mistaken for part of the number.
 * Switching country re-masks whatever has already been typed.
 *
 * The picker is the searchable select rather than a native one, because the
 * catalog is meant to grow past the point where scrolling a list is usable.
 *
 * @param {object} props
 * @param {string} props.value - Masked national number
 * @param {string} props.countryIso2 - Selected calling code, by ISO2
 * @param {(national: string) => void} props.onChange
 * @param {(iso2: string) => void} props.onCountryChange
 * @param {boolean} [props.hasError]
 * @param {boolean} [props.disabled]
 * @param {string} [props.name]
 */
export const PhoneInput = ({
  value,
  countryIso2,
  onChange,
  onCountryChange,
  hasError = false,
  disabled = false,
  name = 'phone',
}) => {
  const country = getPhoneCountry(countryIso2);

  const handleCountryChange = (nextIso2) => {
    onCountryChange(nextIso2);
    // Digits already typed are kept and re-laid out under the new mask.
    onChange(formatNationalPhone(value, nextIso2));
  };

  const handleNumberChange = (event) => {
    const { iso2, national } = parsePhoneEntry(event.target.value, country.iso2);
    // A pasted number carrying a foreign calling code moves the picker too.
    if (iso2 !== country.iso2) onCountryChange(iso2);
    onChange(national);
  };

  return (
    <div
      className={clsx(
        'flex items-stretch w-full bg-ninja-gray/50 dark:bg-[#111] border rounded-xl transition-colors focus-within:border-ninja-green',
        hasError ? 'border-danger' : 'border-border',
        disabled && 'opacity-60'
      )}
    >
      <div className="shrink-0">
        <FilterableSelect
          variant="bare"
          value={country.iso2}
          onChange={handleCountryChange}
          options={PHONE_COUNTRY_OPTIONS}
          disabled={disabled}
          panelMinWidth={COUNTRY_PANEL_MIN_WIDTH}
          searchPlaceholder="Buscar país o código..."
        />
      </div>

      <div className="w-px bg-border my-2 shrink-0" />

      <input
        name={name}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={value}
        onChange={handleNumberChange}
        disabled={disabled}
        placeholder={getPhoneMask(country.iso2)}
        className="flex-1 min-w-0 bg-transparent px-4 py-2.5 text-sm text-ninja-dark dark:text-white outline-none disabled:cursor-not-allowed"
      />
    </div>
  );
};
